import React, {
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import {
  ToolCatalogRow,
  ToolGlyph,
  ToolRowSkeleton,
} from '../components/tool-store/ToolStoreUI';
import { SUPERNOVA_TOOL_CATEGORIES } from '../data/supernovaToolsCatalog';
import { listSupernovaTools } from '../services/supernovaToolsService';
import '../styles/pages/SupernovaToolsPage.css';

const normalize = (value) => value.trim().toLocaleLowerCase();

const SupernovaToolsPage = () => {
  const [tools, setTools] = useState([]);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requestVersion, setRequestVersion] = useState(0);
  const deferredQuery = useDeferredValue(query);
  const searchInputRef = useRef(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const controller = new AbortController();

    const loadCatalogue = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await listSupernovaTools({ signal: controller.signal });
        setTools(response.data);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError('The tool catalogue could not be loaded. Please try again.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadCatalogue();
    return () => controller.abort();
  }, [requestVersion]);

  useEffect(() => {
    const focusSearch = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', focusSearch);
    return () => window.removeEventListener('keydown', focusSearch);
  }, []);

  const normalizedQuery = normalize(deferredQuery);
  const hasFilters = normalizedQuery.length > 0 || activeCategory !== 'all';

  const filteredTools = useMemo(() => {
    return tools.filter((tool) => {
      const matchesCategory =
        activeCategory === 'all' || tool.categories.includes(activeCategory);

      if (!matchesCategory) return false;
      if (!normalizedQuery) return true;

      const searchTarget = [
        tool.title,
        tool.description,
        tool.type,
        tool.availabilityLabel,
        ...tool.categories,
      ]
        .join(' ')
        .toLocaleLowerCase();

      return searchTarget.includes(normalizedQuery);
    });
  }, [activeCategory, normalizedQuery, tools]);

  const clearFilters = () => {
    setQuery('');
    setActiveCategory('all');
    searchInputRef.current?.focus();
  };

  return (
    <PageWrapper>
      <SEO
        title="Supernova Tools | Agents, Chatbots and Guided Tools"
        description="Use Supernova Lab agents, chatbots and guided tools to research, test and turn ideas into action."
      />

      <main className="supernova-tools-page">
        <section className="snt-product-stage" aria-labelledby="supernova-tools-title">
          <div className="snt-shell">
            <div className="snt-hero-grid">
              <motion.div
                className="snt-hero-copy"
                initial={reduceMotion ? false : { opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, ease: 'easeOut' }}
              >
                <h1 id="supernova-tools-title">Supernova Tools</h1>
                <p>
                  Use Supernova Lab’s agents, chatbots and guided tools to
                  research, test and turn ideas into action.
                </p>

                <div className="snt-search">
                  <ToolGlyph name="search" size={25} />
                  <label className="visually-hidden" htmlFor="tool-store-search">
                    Search tools, agents and chatbots
                  </label>
                  <input
                    id="tool-store-search"
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search tools, agents and chatbots"
                    ref={searchInputRef}
                    type="search"
                    value={query}
                  />
                  {query ? (
                    <button
                      aria-label="Clear search"
                      className="snt-search__clear"
                      onClick={() => setQuery('')}
                      type="button"
                    >
                      <ToolGlyph name="close" size={18} />
                    </button>
                  ) : (
                    <kbd aria-label="Keyboard shortcut: Command or Control K">
                      ⌘ K
                    </kbd>
                  )}
                </div>
              </motion.div>
            </div>

            <div className="snt-category-rail" aria-label="Tool categories">
              {SUPERNOVA_TOOL_CATEGORIES.map((category) => (
                <button
                  aria-pressed={activeCategory === category.id}
                  className={
                    activeCategory === category.id
                      ? 'snt-category is-active'
                      : 'snt-category'
                  }
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  type="button"
                >
                  <ToolGlyph name={category.icon} size={21} />
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="snt-catalogue" aria-labelledby="tool-collection-title">
          <div className="snt-shell">
            <div className="snt-section-heading">
              <div>
                <h2 id="tool-collection-title">Explore the collection</h2>
                <p aria-live="polite" role="status">
                  {loading
                    ? 'Loading the catalogue'
                    : `${filteredTools.length} ${
                        filteredTools.length === 1 ? 'tool' : 'tools'
                      } shown`}
                </p>
              </div>

              {hasFilters ? (
                <button className="snt-clear-filters" onClick={clearFilters} type="button">
                  Clear filters
                  <ToolGlyph name="close" size={17} />
                </button>
              ) : null}
            </div>

            {loading ? (
              <div className="snt-tool-list" aria-label="Loading tools">
                {Array.from({ length: 5 }, (_, index) => (
                  <ToolRowSkeleton key={index} />
                ))}
              </div>
            ) : error ? (
              <div className="snt-state-panel" role="alert">
                <ToolGlyph name="info" size={28} />
                <h3>We could not load the tools</h3>
                <p>{error}</p>
                <button
                  onClick={() => setRequestVersion((version) => version + 1)}
                  type="button"
                >
                  Try again
                </button>
              </div>
            ) : filteredTools.length ? (
              <div className="snt-tool-list">
                {filteredTools.map((tool) => (
                  <ToolCatalogRow key={tool.id} tool={tool} />
                ))}
              </div>
            ) : (
              <div className="snt-state-panel">
                <ToolGlyph name="search" size={28} />
                <h3>No tools match those filters</h3>
                <p>Try another search or return to the full collection.</p>
                <button onClick={clearFilters} type="button">
                  Show all tools
                </button>
              </div>
            )}

          </div>
        </section>
      </main>
    </PageWrapper>
  );
};

export default SupernovaToolsPage;
